// Core domain types for KajChi nutrition application

// ===== Food Types =====

export type FoodState = 'raw' | 'cooked' | 'baked'

export interface Food {
  id: string // USDA FDC ID as string
  name: string
  category: string
  state: FoodState
  rawPortionGrams: number
  portionGrams: number // state-adjusted portion grams
  edible: boolean
  plantBased: boolean
  edibleAlone: boolean
  scientificName?: string
  purchaseRawGrams: number
  purchasePrice: number // in original currency units (HUF)
  dailyMinGrams: number
  dailyMaxGrams: number
  purchaseUrl: string
  highFodmap: boolean
  nutrients: Record<number, number> // nutrientId -> amount per 100g
}

// ===== Nutrient Types =====

export type NutrientAlgorithm =
  | 'reachMinDontExceedMax'
  | 'reachMin'
  | 'dontExceedMax'
  | 'ignore'

export type NutrientPriority = 'critical' | 'high' | 'medium' | 'low'

export type NutrientCategory = 'basics' | 'sugars' | 'vitamins' | 'minerals' | 'aminoacids' | 'fattyacids' | 'sterols' | 'magic'

export interface Nutrient {
  id: number
  name: string
  unit: string
  category: NutrientCategory
  priority: NutrientPriority
  algorithm: NutrientAlgorithm
  min?: number
  max?: number
  target?: number
  foodsContain?: number // for statistical algorithms
  avgPer100g?: number // for statistical algorithms
}

// ===== Basket Types =====

export interface Basket {
  [foodId: string]: number // foodId -> grams
}

export interface NutrientIndicator {
  nutrientId: number
  name: string
  unit: string
  value: number
  min?: number
  max?: number
  target?: number
  algorithm: NutrientAlgorithm
  priority: NutrientPriority
  category: NutrientCategory
  passing: boolean // score > 0.75
  score: number
}

export interface FoodOption {
  food: Food
  existingGrams: number
  totalWeightInBasket: number
  inBasket?: boolean // true if food is actually in the basket (even at 0 grams)
}

export interface BasketTotals {
  weight: number // total grams
  price: number // total price in original currency
  score: number // overall nutrition score
}

// ===== Raw Data Types (for CSV parsing) =====

export interface FoodMeta {
  'Food Name': string
  'Last Modified': string
  Status: string
  Category: string
  'USDA Raw': string
  'USDA Cooked': string
  'USDA Baked': string
  'Scientific Name': string
  'Plant Based': string // 'checked' or empty
  'Edible Alone': string // 'checked' or empty
  'Edible Raw': string // 'checked' or empty
  'Edible Cooked': string // 'checked' or empty
  'Edible Baked': string // 'checked' or empty
  'Raw Portion Grams': string
  'Cooked Weight Ratio': string
  'Baked Weight Ratio': string
  'Shelf Life Raw': string
  'Shelf Life Cooked': string
  'Shelf Life Baked': string
  'Daily Min Grams': string
  'Daily Max Grams': string
  'Purchase URL': string
  'Purchase Raw Grams': string
  'Purchase Price': string
  'High FODMAP': string
}

export interface NutrientMeta {
  'FDC Nutrient ID': string
  Status: string
  Priority: string
  Category: string
  Name: string
  Unit: string
}

// ===== User Profile Types =====

export type Sex = 'male' | 'female'
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'veryActive'
export type DietType = 'omnivore' | 'vegetarian' | 'vegan'
export type PregnancyStatus = false | 'trimester1' | 'trimester2' | 'trimester3'

export interface UserProfile {
  age: number
  sex: Sex
  weightKg: number
  heightCm: number
  activityLevel: ActivityLevel
  pregnant: PregnancyStatus
  lactating: boolean
  smoker: boolean
  dietType: DietType
}

export const DEFAULT_USER_PROFILE: UserProfile = {
  age: 30,
  sex: 'male',
  weightKg: 80,
  heightCm: 180,
  activityLevel: 'light',
  pregnant: false,
  lactating: false,
  smoker: false,
  dietType: 'omnivore',
}

export interface EstimatedTargets {
  min: number | null
  recommended: number | null
  ul: number | null
  ld50: number | null
  algorithm: NutrientAlgorithm
}

// ===== USDA Data Types =====

export interface USDANutrient {
  nutrient: {
    id: number
    number: string
    name: string
    rank?: number
    unitName: string
  }
  amount?: number
  type: string
  id?: number
  dataPoints?: number
  foodNutrientDerivation?: {
    id: number
    code: string
    description: string
    foodNutrientSource?: {
      id: number
      code: string
      description: string
    }
  }
}

export interface USDAFoodPortion {
  id?: number
  gramWeight: number
  sequenceNumber?: number
  amount: number
  modifier?: string
  measureUnit?: {
    id: number
    name: string
    abbreviation: string
  }
}

export interface USDAFood {
  fdcId: number
  description: string
  dataType: string // 'SR Legacy', 'Survey', 'Foundation', 'Branded'
  publicationDate?: string
  foodClass?: string
  scientificName?: string
  foodNutrients: USDANutrient[]
  foodPortions?: USDAFoodPortion[]
  servingSize?: number // for Branded foods
  servingSizeUnit?: string // for Branded foods
  householdServingFullText?: string // for Branded foods
  brandOwner?: string // for Branded foods
  brandName?: string // for Branded foods
  ingredients?: string // for Branded foods
  ndbNumber?: number
  isHistoricalReference?: boolean
  foodCategory?: {
    id: number
    code: string
    description: string
  }
  nutrientConversionFactors?: Array<{
    id: number
    type: string
    name: string
    proteinValue?: number
    fatValue?: number
    carbohydrateValue?: number
    value?: number
  }>
}

// ===== Generated Data Types =====

export interface GeneratedData {
  foods: Food[]
  nutrients: Nutrient[]
  buildInfo: BuildInfo
}

export interface BuildInfo {
  timestamp: string
  foodCount: number
  nutrientCount: number
  version: string
  source: {
    foodsFile: string
    nutrientsFile: string
    usdaFileCount: number
  }
}

// ===== Custom Preferences Types =====

export interface CustomNutrientConstraints {
  [nutrientId: number]: {
    algorithm?: NutrientAlgorithm
    min?: number
    max?: number
    target?: number
  }
}

export interface CustomPortions {
  [foodId: string]: number // custom portion grams per food
}

export interface CustomPurchaseInfo {
  [foodId: string]: {
    purchaseRawGrams: number
    purchasePrice: number
  }
}

export interface UserPreferences {
  nutrientConstraints: CustomNutrientConstraints
  customPortions?: CustomPortions
  customPurchaseInfo?: CustomPurchaseInfo
  basket: Basket
  basketOrder?: string[] // food IDs in display order
  userProfile?: UserProfile
  timestamp: string
}

// ===== Scoring Types =====

export interface ScoringContext {
  basket: Basket
  foods: Food[]
  nutrients: Nutrient[]
  estimatedConstraints?: Record<number, EstimatedTargets>
  customConstraints?: CustomNutrientConstraints
  customPortions?: CustomPortions
  customPurchaseInfo?: CustomPurchaseInfo
}

export interface ScoreResult {
  indicators: NutrientIndicator[]
  foodOptions: FoodOption[]
  totals: BasketTotals
  overallScore: number
}

// ===== Utility Functions =====

export function getPortionPrice(food: Food): number {
  if (food.purchaseRawGrams <= 0) return 0
  return (food.purchasePrice * food.rawPortionGrams) / food.purchaseRawGrams
}

export function getFoodPrice(food: Food, grams: number): number {
  if (food.purchaseRawGrams <= 0 || food.portionGrams <= 0) return 0
  return (grams * food.purchasePrice * food.rawPortionGrams) / (food.portionGrams * food.purchaseRawGrams)
}